import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { DRStatusBadge, PRStatusBadge } from "../components/etams/StatusBadge";
import { Input } from "../components/ui/input";
import { DR_STATUS_LABEL } from "../lib/statusLabels";
import { toast } from "sonner";
import IsianTransferManual, { isianTransferAwal, isianTransferLengkap, muatanBukti } from "../components/etams/IsianTransferManual";
import TagihanRevisiPanel from "../components/etams/TagihanRevisiPanel";
import KirimUlangRuangPesanan from "../components/etams/KirimUlangRuangPesanan";
import StatusWaCustomer from "../components/etams/StatusWaCustomer";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { formatIDR } from "../lib/format";
import { jamPesanan, useStoreTimezones } from "../lib/timezone";
import { labelPaket, labelProduk } from "../lib/faKonfigurasi";
import Hint from "../components/etams/Hint";
import { FA_CREATE_ROLES, FA_DETAIL_ROLES } from "../lib/roles";

const TANDA_JADI_AMOUNT = 200_000;

// Peran yang backend izinkan menerbitkan tautan (`PENERBIT_ROLES` di
// `routers/payments_qris.py`). ⚠ Ini SALINAN untuk tampilan, BUKAN pagar —
// penjaganya tetap role guard di router. Disalin supaya peran yang nol boleh
// nol melihat tombol yang pasti menolaknya, bukan supaya ia aman.
const PENERBIT_QRIS = ["CS", "KEPALA_CABANG", "OWNER"];

// Galat backend -> kalimat yang bisa dibaca staf. Yang nol terdaftar jatuh ke
// kalimat umum; menampilkan kode mentah membuat orang menelepon, bukan bertindak.
const PESAN_GALAT_QRIS = {
  ROLE_TIDAK_BOLEH_MENERBITKAN: "Peran Anda tidak boleh menerbitkan tautan pembayaran. Minta CS atau Kepala Cabang Anda.",
  STORE_SCOPE: "PD ini milik cabang lain. Kalau perlu, minta Kepala Cabang Anda.",
  REGION_SCOPE: "PD ini milik cabang lain. Kalau perlu, minta Kepala Cabang pemilik PD atau Owner.",
  TANDA_JADI_ALREADY_SUBMITTED: "Tanda Jadi sudah diajukan untuk PD ini.",
  GATEWAY_GAGAL: "Tautan pembayaran belum terbit",
  DR_NOT_FOUND: "PD-nya tidak ditemukan.",
};
// T81: untuk kode ini server mengirim RINGKASAN alasan sehari-hari (bukan kode mentah) — ditampilkan di bawah judul.
// Rincian teknis DOKU tercatat di Jejak Audit (PAYMENT_GATEWAY_GAGAL) untuk Owner.
const GALAT_BERALASAN = ["GATEWAY_GAGAL"];

export default function DesignRequestDetail() {
  const { id } = useParams();
  const { profile } = useAuth();
  const [dr, setDr] = useState(null);
  useStoreTimezones();   // T93: jam menurut zona cabang PD + label WIB/WITA
  const jamPd = (d) => jamPesanan(d, dr?.store_id, dr?.region_id);
  const [customer, setCustomer] = useState(null);
  // T64: desain disetujui customer belum tentu sudah punya berkas final untuk cetak.
  const [finalAda, setFinalAda] = useState(null);
  const disetujui = dr?.dr_status === "DESIGN_APPROVED";
  useEffect(() => {
    if (!disetujui || !dr?.id) return;
    api.get("/design-files", { params: { design_request_id: dr.id } })
      .then((r) => setFinalAda((r.data?.items || []).some((f) => f.is_final_selected)))
      .catch((e) => { setFinalAda(null); toast.error(apiErrorMessage(e, "Status berkas final gagal dimuat.")); });
  }, [disetujui, dr?.id]);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // K234 / BUSINESS-113: isian transfer manual disederhanakan — `IsianTransferManual` (wajib hanya bukti).
  const [isianTj, setIsianTj] = useState(isianTransferAwal);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // ⛔ Tanda Jadi punya DUA jalur, dan sampai 3 September 2026 layar ini cuma
  //    punya satu. `POST /api/payments/tanda-jadi/qris` sudah lengkap di
  //    backend sejak W2 — pagar satu-tanda-jadi-per-DR, cakupan store/region,
  //    pembatalan catatan kalau gateway gagal — tapi NOL satu pun layar
  //    memanggilnya. Sapuan endpoint-yatim menemukannya, dan itu juga sebab
  //    keempat env DOKU nol pernah diisi: jalurnya nol pernah dipakai.
  const [metode, setMetode] = useState("TRANSFER");   // TRANSFER | QRIS
  const [qris, setQris] = useState(null);             // hasil penerbitan tautan
  const [qrisSibuk, setQrisSibuk] = useState(false);
  const [tersalin, setTersalin] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/design-requests/${id}`);
      setDr(r.data);
      // Backend returns plain DR row (no joined customer). Fetch customer separately.
      if (r.data?.customer_id) {
        try {
          const c = await api.get(`/customers/${r.data.customer_id}`);
          setCustomer(c.data);
        } catch { /* ignore — show id only */ }
      }
      if (r.data?.tanda_jadi_payment_record_id) {
        const pr = await api.get(`/payments/${r.data.tanda_jadi_payment_record_id}`);
        setPayment(pr.data);
      } else {
        setPayment(null);
      }
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat PD."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Realtime channels (additive — runs alongside 8s polling until Owner 2-tab verifies).
  useRealtimeTable("design_requests", load);
  // K206 / VONIS-HAK-BACA-ISI-FA: kanal `payment_records` membawa baris utuh (nominal) — hanya pembaca tagihan
  // (cermin `TAGIHAN_BACA_ROLES` routers/payments.py). Peran lain cukup polling; rutenya memberi keadaan tanpa nominal.
  useRealtimeTable("payment_records", load,
    { enabled: ["OWNER", "KEPALA_CABANG", "CS", "FINANCE"].includes(profile?.role?.code) });

  // Lightweight refetch every 8s while page open — no Supabase channel yet (C3a-2)
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  async function submitTandaJadi() {
    setSubmitting(true);
    setApiError(null);
    try {
      const body = {
        payment_type: "TANDA_JADI",
        payment_method: "TRANSFER",
        amount_idr: TANDA_JADI_AMOUNT,
        proof_category: "TRANSFER_TANDA_JADI",
        design_request_id: id,
        proof: muatanBukti(isianTj),
      };
      const r = await api.post("/payments/submit", body);
      toast.success("Tanda Jadi berhasil dikirim, menunggu konfirmasi Owner/Kepala Cabang.");
      setPayment(r.data.payment_record);
      load();
    } catch (e) {
      setApiError({
        error: e.response?.data?.detail?.error,
        message: apiErrorMessage(e, "Tanda Jadi gagal dikirim. Coba lagi."),
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function terbitkanTautanQris() {
    setQrisSibuk(true);
    setApiError(null);
    try {
      const r = await api.post("/payments/tanda-jadi/qris", { design_request_id: id });
      setQris(r.data);
      toast.success("Tautan pembayaran terbit. Kirimkan ke customer.");
      load();
    } catch (e) {
      // Backend memulangkan galat bernama; diterjemahkan jadi kalimat yang
      // bisa dibaca staf, bukan kode mentah. `GATEWAY_GAGAL` sengaja disebut
      // apa adanya — kalau DOKU menolak, yang perlu tahu adalah orangnya,
      // bukan cuma log.
      const d = e.response?.data?.detail;
      setApiError({
        error: d && typeof d === "object" ? d.error : undefined,
        message: apiErrorMessage(e, "Gagal menerbitkan tautan pembayaran."),
      });
    } finally {
      setQrisSibuk(false);
    }
  }

  async function salinTautan(url) {
    // ⚠ URL DITERIMA SEBAGAI ARGUMEN, bukan dibaca dari `qris`. Versi pertama
    //   perbaikan 5 Sep menukar tulisannya ke `tautanBayar` tapi MENINGGALKAN
    //   penjaganya di `qris?.url` — sehingga tombol Salin MATI justru untuk
    //   tautan yang dibaca dari database, yaitu kasus yang perbaikan ini ada
    //   untuk melayani. Argumen menutupnya: nol ada dua sumber yang bisa
    //   berpisah.
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setTersalin(true);
      setTimeout(() => setTersalin(false), 2000);
    } catch {
      // Clipboard bisa ditolak browser (konteks non-HTTPS, izin dicabut).
      // Tautannya tetap terlihat di layar dan bisa disalin tangan — jadi
      // kegagalan ini nol boleh terbaca seperti tautannya yang gagal.
      toast.error("Browser menolak menyalin. Salin tautannya dari layar.");
    }
  }

  if (loading && !dr) {
    return <div className="text-sm text-muted-foreground" data-testid="dr-detail-loading">Memuat...</div>;
  }
  if (err) {
    return (
      <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm text-danger-soft-foreground">
        {err}
      </div>
    );
  }
  if (!dr) return null;

  const canSubmit = !payment && ["DESIGN_REQUEST_CREATED", "MENUNGGU_TANDA_JADI"].includes(dr.dr_status);
  const tandaJadiSubmitted = !!payment;
  // T78: tanda jadi sudah DIKONFIRMASI → kotak hanya menyebut diterima kapan + cara bayar (tanpa tautan/ajakan kirim).
  const tandaJadiDiterima = payment?.status === "DIKONFIRMASI";
  const bolehTerbitkanQris = PENERBIT_QRIS.includes(profile?.role?.code);

  // ⛔ TAUTAN BAYAR KINI BERTAHAN MELEWATI HALAMAN DITUTUP — migration
  //    `20260905090000_tautan_bayar_tersimpan` (5 September 2026).
  //
  //    Versi 4 September menampilkannya HANYA dari state `qris`, yaitu jawaban
  //    penerbitan yang barusan. Sekali halaman ditutup atau di-refresh,
  //    tautannya nol bisa dipanggil kembali — dan pagar
  //    `TANDA_JADI_ALREADY_SUBMITTED` menolak menerbitkan yang baru, sehingga
  //    CS harus MEMBATALKAN tagihan yang sah untuk membuat tagihan yang sama.
  //
  // ⚠ `qris` didahulukan, bukan `payment`: sesaat sesudah penerbitan, `load()`
  //   belum tentu selesai, dan yang barusan diterima PASTI yang terbaru.
  //   Nomor invoice DITURUNKAN dari id (`TJ-<id>`, lihat `doku_invoice.py`) —
  //   `payment_records` sengaja nol menyimpannya sebagai kolom tersendiri.
  // K220: `qris` yang baru terbit tidak pernah dianggap lewat; tautan tersimpan dinilai dari batas berlakunya.
  const tautanLewat = (t, p) => !qris && !!t?.kedaluwarsa && p?.status === "MENUNGGU_KONFIRMASI"
    && new Date(t.kedaluwarsa).getTime() <= Date.now();
  const tautanBayar = qris || (payment?.gateway_url ? {
    invoice_number: `TJ-${payment.id}`,
    amount_idr: payment.amount_idr,
    url: payment.gateway_url,
    kedaluwarsa: payment.gateway_expires_at,
  } : null);

  return (
    <div className="space-y-4" data-testid="dr-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Permintaan Desain</div>
          <h1 className="text-2xl font-bold" data-testid="dr-number">{dr.dr_number}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {customer?.name || "—"} · {labelProduk(dr.product_summary_type)} · {labelPaket(dr.package_type)}
          </div>
        </div>
        <DRStatusBadge value={dr.dr_status} testId="dr-status-badge" />
      </div>

      {/* U2 NASKAH-UAT (27 Sep 2026): layar Buat FA berkata "Buka layar ini dari halaman Permintaan Desain", tetapi
          halaman ini dulu tidak punya tombolnya — CS tidak bisa sampai ke pembuatan FA lewat layar. */}
      {disetujui && finalAda === false && (
        <p className="text-sm rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-3 py-2" data-testid="dr-final-belum">
          Desain sudah disetujui customer — menunggu desainer mengunggah berkas final untuk cetak.
        </p>
      )}
      {dr.dr_status === "DESIGN_APPROVED" && !dr.promoted_fa_id && FA_CREATE_ROLES.includes(profile?.role?.code) && (
        <Button asChild data-testid="dr-buat-fa-btn">
          <Link to={`/fa-orders/new?dr=${dr.id}`}>Buat FA dari PD ini</Link>
        </Button>
      )}
      {dr.promoted_fa_id && FA_DETAIL_ROLES.includes(profile?.role?.code) && (
        <Button asChild variant="outline" data-testid="dr-buka-fa-btn">
          <Link to={`/fa-orders/${dr.promoted_fa_id}`}>Buka FA pesanan ini</Link>
        </Button>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Detail PD</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <div><strong>Qty Atasan:</strong> {dr.preliminary_qty_atasan}
            {dr.preliminary_qty_celana > 0 && ` · Qty Celana: ${dr.preliminary_qty_celana}`}</div>
          <div><strong>Repeat Order:</strong> {dr.is_repeat_order ? "Ya" : "Tidak"}</div>
          {dr.notes && <div><strong>Catatan:</strong> {dr.notes}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Tanda Jadi</span>
            {payment && (
              <PRStatusBadge value={payment.status} />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tandaJadiDiterima ? (
            /* T78 (titipan auditor 27 Okt 2026): sesudah DIKONFIRMASI kotak ini hanya menyebut yang sudah terjadi —
               dulu tautan DOKU kedaluwarsa, "Kirim tautan ini ke customer", dan "Menunggu konfirmasi Owner/Kepala
               Cabang" tetap tampil. Nominal = uang yang benar-benar diterima (K227); kekurangan ditagih di pelunasan. */
            <div className="text-sm space-y-1" data-testid="tj-diterima">
              <div className="font-medium text-success-soft-foreground">
                Tanda jadi diterima {jamPd(payment.confirmed_at)}
                {payment.metode_label ? ` · ${payment.metode_label}` : ""}
              </div>
              {typeof payment.amount_idr === "number" && (
                <div data-testid="tanda-jadi-amount">
                  {formatIDR(payment.amount_idr)}
                  {payment.amount_idr < TANDA_JADI_AMOUNT && (
                    <span className="text-xs text-warning-soft-foreground" data-testid="tj-diterima-kurang">
                      {" "}— kurang {formatIDR(TANDA_JADI_AMOUNT - payment.amount_idr)} dari {formatIDR(TANDA_JADI_AMOUNT)},
                      ditambahkan ke tagihan pelunasan.
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
          <div className="text-sm">
            <strong>Nominal:</strong>{" "}
            <span data-testid="tanda-jadi-amount">{formatIDR(TANDA_JADI_AMOUNT)}</span>{" "}
            <span className="text-xs text-muted-foreground">(hanya baca sesuai aturan resmi)</span>
          </div>
          )}

          {!tandaJadiSubmitted && canSubmit && bolehTerbitkanQris && (
            <div className="flex gap-2" data-testid="tj-pilih-metode">
              {[["TRANSFER", "Transfer manual"], ["QRIS", "Tagih lewat DOKU"]].map(([nilai, label]) => (
                <Button
                  key={nilai}
                  type="button"
                  variant={metode === nilai ? "default" : "outline"}
                  size="sm"
                  aria-pressed={metode === nilai}
                  onClick={() => { setMetode(nilai); setApiError(null); }}
                  data-testid={`tj-metode-${nilai.toLowerCase()}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          )}

          {!tandaJadiSubmitted && canSubmit && metode === "QRIS" && (
            <div className="space-y-3" data-testid="tj-qris-panel">
                  <p className="text-sm text-muted-foreground">
                    Sistem menerbitkan tautan pembayaran senilai{" "}
                    {formatIDR(TANDA_JADI_AMOUNT)} untuk dikirim ke customer.{" "}
                    <Hint>Pembayaran lewat tautan ini terkonfirmasi sendiri, tanpa bukti transfer diketik ulang.</Hint>
                  </p>
                  {apiError && (
                    <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm" data-testid="tj-qris-error">
                      <div className="font-bold text-danger-soft-foreground">{PESAN_GALAT_QRIS[apiError.error] || "Gagal menerbitkan tautan"}</div>
                      {apiError.message && (!PESAN_GALAT_QRIS[apiError.error] || GALAT_BERALASAN.includes(apiError.error)) && (
                        <div className="text-danger-soft-foreground mt-1 whitespace-pre-wrap">{apiError.message}</div>
                      )}
                    </div>
                  )}
                  <Button
                    data-testid="tj-terbitkan-qris"
                    disabled={qrisSibuk}
                    onClick={terbitkanTautanQris}
                  >
                    {qrisSibuk ? "Menerbitkan…" : "Terbitkan tautan pembayaran"}
                  </Button>
            </div>
          )}

          {/* ⛔ BLOK INI SENGAJA DI LUAR gerbang `canSubmit`/`tandaJadiSubmitted`,
              dan itu perbaikan atas cacat yang ditemukan lewat LAYAR, bukan test.

              Versi pertama menaruhnya di dalam gerbang. Begitu tautan terbit,
              `load()` menyegarkan DR, `tandaJadiSubmitted` jadi TRUE, seluruh
              blok pengajuan menghilang — dan TAUTANNYA IKUT HILANG sebelum CS
              sempat menyalinnya.

              ⚠ Itu bukan sekadar merepotkan: tautan pembayaran NOL TERSIMPAN
              DI MANA PUN. Diukur 4 September 2026 — `payment_records` nol punya
              kolomnya, dan nol ada tabel lain yang memuatnya. Sekali hilang dari
              layar, ia nol bisa dipanggil kembali; dan pagar
              `TANDA_JADI_ALREADY_SUBMITTED` menolak menerbitkan yang baru sampai
              yang lama ditolak dulu.

              Perbaikan sesungguhnya adalah MENYIMPAN tautannya (butuh migration
              -> tangan Owner). Sampai itu ada, blok ini menahannya di layar
              selama sesi dan mengatakan terus terang bahwa ia nol bisa
              ditampilkan lagi. */}
          {tautanBayar && !tandaJadiDiterima && (
                <div className="border border-border rounded p-3 space-y-2 text-sm" data-testid="tj-qris-hasil">
                  <div>
                    <strong>No. Tagihan:</strong>{" "}
                    <span className="tabular-nums" data-testid="tj-qris-invoice">{tautanBayar.invoice_number}</span>
                  </div>
                  <div><strong>Nominal:</strong> {formatIDR(tautanBayar.amount_idr ?? TANDA_JADI_AMOUNT)}</div>
                  {tautanBayar.kedaluwarsa && (
                    <div><strong>Berlaku sampai:</strong> {jamPd(tautanBayar.kedaluwarsa)}</div>
                  )}
                  {/* K220 (BUSINESS-96): tautan lewat 24 jam → terbitkan ulang. Server menanyakan DOKU dulu: yang belum
                      dibayar ditutup "Kedaluwarsa" lalu tautan baru terbit; yang ternyata sudah dibayar tidak disentuh. */}
                  {tautanLewat(tautanBayar, payment) && bolehTerbitkanQris && (
                    <div className="rounded-md border border-warning-soft-border bg-warning-soft px-2 py-1.5 text-warning-soft-foreground"
                         data-testid="tj-qris-lewat">
                      Tautan ini sudah tidak berlaku.{" "}
                      <Button type="button" size="sm" variant="outline" disabled={qrisSibuk}
                              onClick={terbitkanTautanQris} data-testid="tj-qris-terbitkan-ulang">
                        {qrisSibuk ? "Menerbitkan…" : "Terbitkan ulang tautan"}
                      </Button>
                    </div>
                  )}
                  {/* K217: cara bayar yang ditawarkan tautan ini (hanya ada sesaat sesudah diterbitkan). */}
                  {Array.isArray(tautanBayar.metode) && tautanBayar.metode.length > 0 && (
                    <div data-testid="tj-qris-metode"><strong>Cara bayar:</strong> {tautanBayar.metode.join(", ")}</div>
                  )}
                  <div className="flex items-center gap-2">
                    <Input readOnly value={tautanBayar.url} className="font-mono text-xs" data-testid="tj-qris-url" />
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => salinTautan(tautanBayar.url)}
                            data-testid="tj-qris-salin">
                      {tersalin ? "Tersalin" : "Salin"}
                    </Button>
                  </div>
                  {/* T43 (BUSINESS-103): tautan dikirim otomatis ke WhatsApp customer saat terbit; Salin = cadangan. */}
                  <p className="text-xs text-muted-foreground" data-testid="tj-qris-wa">
                    {tautanBayar.wa === "DIANTRE"
                      ? "Tautan ini sudah dikirim otomatis ke WhatsApp customer. Tombol Salin untuk mengirim ulang bila perlu."
                      : "Kirim tautan ini ke customer."}{" "}
                    <Hint>Begitu customer membayar, Tanda Jadi terkonfirmasi sendiri — tidak perlu dikonfirmasi manual.</Hint>
                  </p>
                  {/* ⛔ PERINGATAN "salin sekarang, belum tersimpan" DICABUT
                      5 September 2026 — sebabnya hilang. Tautannya kini
                      tersimpan di `payment_records.gateway_url` dan dibaca
                      ulang tiap halaman dibuka.

                      Peringatan yang bertahan sesudah sebabnya hilang melatih
                      orang mengabaikan SELURUH daftarnya — pelajaran yang
                      sudah dibayar repo ini di `ALAMAT_BELUM_PUNYA_RUMAH`. */}
                </div>
          )}

          {!tandaJadiSubmitted && canSubmit && metode === "TRANSFER" && (
            <>
              <IsianTransferManual storeId={dr.store_id} isian={isianTj} setIsian={setIsianTj} testIdPrefix="tj" />

              {apiError && (
                <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm" data-testid="tj-api-error">
                  <div className="font-bold text-danger-soft-foreground">Tanda Jadi belum terkirim</div>
                  {apiError.message && (
                    <div className="text-danger-soft-foreground mt-1 whitespace-pre-wrap">{apiError.message}</div>
                  )}
                </div>
              )}

              <Button
                data-testid="submit-tanda-jadi"
                disabled={submitting || !isianTransferLengkap(isianTj)}
                onClick={submitTandaJadi}
              >
                {submitting ? "Mengirim..." : "Kirim Tanda Jadi"}
              </Button>
            </>
          )}

          {tandaJadiSubmitted && !tandaJadiDiterima && payment.status !== "DITOLAK" && (
            <div className="text-sm text-muted-foreground">
              Tanda Jadi sudah dikirim pada{" "}
              <strong>{jamPd(payment.submitted_at)}</strong>.
              Menunggu konfirmasi Owner/Kepala Cabang.
            </div>
          )}

          {!canSubmit && !tandaJadiSubmitted && (
            <div className="text-sm text-muted-foreground">
              PD sudah lewat tahap Tanda Jadi ({DR_STATUS_LABEL[dr.dr_status] || "—"}).
            </div>
          )}
        </CardContent>
      </Card>

      {dr.tanda_jadi_confirmed_at && dr.dr_status !== "CANCELLED"
        && ["CS", "KEPALA_CABANG", "OWNER"].includes(profile?.role?.code) && (
        <KirimUlangRuangPesanan drId={id} />
      )}

      {/* T45: status tiap WhatsApp ke customer PD ini (Owner · KC · CS) — "Terkirim" belum berarti sampai. */}
      <StatusWaCustomer sumber="dr" id={id} />

      {["CS", "KEPALA_CABANG", "OWNER", "FINANCE"].includes(profile?.role?.code) && (
        <TagihanRevisiPanel drId={id} roleCode={profile?.role?.code} />
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Tahap Berikutnya</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Desainer mengklaim tugas · unggah hasil desain · persetujuan desain akhir · draf FA.
        </CardContent>
      </Card>
    </div>
  );
}
